import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import React from 'react';

// ── Error Boundary ──────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#F7F9FB', padding: '24px', fontFamily: 'sans-serif' }}>
          <h2 style={{ color: '#0f172a', marginBottom: '8px' }}>Something went wrong</h2>
          <p style={{ color: '#64748b', marginBottom: '16px', textAlign: 'center' }}>Please refresh the page. If the problem persists, clear your browser cache.</p>
          <button onClick={() => window.location.reload()} style={{ background: '#0f172a', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '999px', cursor: 'pointer', fontWeight: 600 }}>Refresh</button>
          {process.env.NODE_ENV !== 'production' && <pre style={{ marginTop: 16, fontSize: 12, color: '#ef4444', maxWidth: 480, overflow: 'auto' }}>{String(this.state.error)}</pre>}
        </div>
      );
    }
    return this.props.children;
  }
}
// ─────────────────────────────────────────────────────────────────────────────
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { I18nProvider } from './contexts/I18nContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { PWAProvider, usePWA } from './contexts/PWAContext';
import InstallModal from './components/InstallModal';
import InstallBanner from './components/InstallBanner';
import AuthPage from './pages/AuthPage';
import AuthCallback from './pages/AuthCallback';
import HomePage from './pages/HomePage';
import AddReminderPage from './pages/AddReminderPage';
import AllRemindersPage from './pages/AllRemindersPage';
import ReminderDetailPage from './pages/ReminderDetailPage';
import GroupsPage from './pages/GroupsPage';
import SettingsPage from './pages/SettingsPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AdminPage from './pages/AdminPage';
import InvitePage from './pages/InvitePage';
import RecentRemindersPage from './pages/RecentRemindersPage';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full" data-testid="loading-spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

function AppRoutes() {
  const location = useLocation();
  const { user } = useAuth();
  const { triggerPostAuthModal, initBanner } = usePWA();
  const prevUserRef = useRef(null);

  // Trigger post-login modal when user state transitions null → authenticated
  useEffect(() => {
    if (user && !prevUserRef.current) {
      triggerPostAuthModal();
    }
    prevUserRef.current = user || null;
  }, [user]); // eslint-disable-line

  // Init session banner whenever user is authenticated
  useEffect(() => {
    if (user) initBanner();
  }, [user]); // eslint-disable-line

  // Check URL fragment for Google OAuth session_id (synchronous, before routes render)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
      <Routes>
        <Route path="/" element={<AuthPage />} />
        <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
        <Route path="/add" element={<ProtectedRoute><AddReminderPage /></ProtectedRoute>} />
        <Route path="/all-reminders" element={<ProtectedRoute><AllRemindersPage /></ProtectedRoute>} />
        <Route path="/recent" element={<ProtectedRoute><RecentRemindersPage /></ProtectedRoute>} />
        <Route path="/reminder/:reminderId" element={<ProtectedRoute><ReminderDetailPage /></ProtectedRoute>} />
        <Route path="/edit-reminder/:reminderId" element={<ProtectedRoute><AddReminderPage /></ProtectedRoute>} />
        <Route path="/groups" element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} />
        <Route path="/groups/:groupId" element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/invite/:groupId" element={<InvitePage />} />
      </Routes>
      {/* Global PWA install UI — renders above all pages */}
      <InstallModal />
      <InstallBanner />
    </>
  );
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <I18nProvider>
          <AuthProvider>
            {GOOGLE_CLIENT_ID ? (
              <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                <PWAProvider>
                  <SettingsProvider>
                    <AppRoutes />
                  </SettingsProvider>
                </PWAProvider>
              </GoogleOAuthProvider>
            ) : (
              <PWAProvider>
                <SettingsProvider>
                  <AppRoutes />
                </SettingsProvider>
              </PWAProvider>
            )}
          </AuthProvider>
        </I18nProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default function AppWithBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}
